import { rightFormatTagForText } from '@nixi1/nx-channel-utils'
import _get from 'lodash.get'

import { getActiveUserFromState, getActiveUserId } from '../actionsHelpers'
import apiConversation from 'api/conversation'
import resource from 'api/resource'

/**
 * Available actions for ChatComposer component.
 * Each action needs the identification of the user, to know its current configuration.
 */
import * as types from '../../types/conversations'
import { verifyFileVirus } from 'utils/FileUtils'

const conversationsApi = apiConversation.conversations

const uploading = (userId) => ({ userId, type: types.COMPOSER_UPLOADING_FILES })
const send = (userId) => ({ userId, type: types.COMPOSER_SEND })

/**
 * Sent action, if there are errors, show them
 * @param {Object} errors
 */
const sent = (userId, errors) => ({ userId, errors, type: types.COMPOSER_SENT })

/**
 * Enable or disable note dash
 */
const changeMessageType = (messageType) => (dispatch, getState) =>
  dispatch({
    messageType,
    userId: getActiveUserId(getState),
    type: types.COMPOSER_CHANGE_MESSAGE_TYPE,
  })

/**
 * Reset composer
 */
const resetComposer = ({ userId, persistFields }) => (dispatch, getState) => {
  dispatch({
    persistFields,
    userId: userId || getActiveUserId(getState),
    type: types.COMPOSER_RESET
  })
}

/**
 * Set message from template
 * @param {Object} template
 * @param {templateId} selectedTemplate
 */
const setMessageFromTemplate = (selectedTemplate, template) => (dispatch, getState) => {
  const {
    brands, delegations,
    marketing: { templates },
    conversations,
    tabs: { activeUser },
  } = getState()
  const { company } = templates
  const { user } = conversations[activeUser]

  // Template not selected
  if (!selectedTemplate && !template) {
    dispatch({
      userId: activeUser,
      type: types.COMPOSER_SET_TEMPLATE,
      payload: {
        selectedTemplate: '',
        message: '',
        buttons: [],
        files: [],
        variables: []
      }
    })

    return ''
  }

  user.brandName = _get(brands, `${user.brand}.name`, '')
  user.delegationName = _get(delegations, `${user.delegation}.name`, '')

  const data = { user, companyName: company.name }

  let message = template.variables.reduce((a, c) =>
    a.replaceAll(c.name, _get(data, c.field, c.name))
  , template.contentHtml || template.content || '')

  message = message.replace(/&amp;nbsp;/g, ' ')
  message = message.replace(/&nbsp;/g, ' ')

  dispatch({
    userId: activeUser,
    type: types.COMPOSER_SET_TEMPLATE,
    payload: {
      selectedTemplate,
      message,
      buttons: template.buttons,
      files: template.attachments,
      variables: template.variables || []
    }
  })

  return message
}

const setRelatedTag = (tag) => (dispatch, getState) => {
  dispatch({
    userId: getActiveUserId(getState),
    type: types.COMPOSER_SET_TAG,
    tag,
  })
}

/**
 * Set reply messages
 * @param {object} replyMessage
 * @param {string} replyMessage.id
 * @param {string} [replyMessage.text]
 * @param {[object]} [replyMessage.attachments]
 * @param {[object]} [replyMessage.buttons]
 * @param {string} [replyMessage.date]
 *
 * @returns {boolean}
 */
const setReply = (replyMessage) => (dispatch, getState) => {
  const { id, text, attachments, buttons, date } = replyMessage

  if (!id) return false

  const replyInfo = {}
  if (text && typeof text === 'string') {
    replyInfo.text = text
  }

  if (date && typeof date === 'string') {
    replyInfo.date = date
  }

  if (Array.isArray(attachments) && attachments.length) {
    replyInfo.attachments = attachments
  }

  if (Array.isArray(buttons) && buttons.length) {
    replyInfo.buttons = buttons
  }

  dispatch({
    userId: getActiveUserId(getState),
    type: types.COMPOSER_SET_REPLY,
    inReplyTo: id,
    replyInfo
  })

  return true
}

/**
 * Remove Reply message
 * @returns {boolean}
 */
const removeReply = () => (dispatch, getState) => {
  dispatch({
    userId: getActiveUserId(getState),
    type: types.COMPOSER_REMOVE_REPLY
  })

  return true
}

/**
 * @param {String} message
 */
const updateMessage = (message) => (dispatch, getState) => {
  const user = getActiveUserFromState(getState)
  const userId = getActiveUserId(getState)

  const { channel, provider } = user?.channels?.[0] || {}
  const isTwWa = provider === 'twilio' && channel === 'whatsapp'

  dispatch({
    userId,
    type: types.COMPOSER_EDIT_MESSAGE,
    message: isTwWa ? message : rightFormatTagForText(message)
  })
}

/**
 * @param {String} addExternalText
 */
const addExternalText = (addExternalText) => (dispatch, getState) =>
  dispatch({ addExternalText, userId: getActiveUserId(getState), type: types.COMPOSER_ADD_EXTERNAL_TEXT })

const updateMsgFromExternalText = (extraText) => (dispatch, getState) => {
  const userId = getActiveUserId(getState)
  const message = getState().conversations?.[userId]?.composer?.message || ''

  dispatch({
    userId,
    addExternalText: `${message} ${extraText}`,
    type: types.COMPOSER_ADD_EXTERNAL_TEXT
  })
}

/**
 * @param {Object} chatmailHeader
 */
const setChatmailHeader = (chatmailHeader) => (dispatch, getState) =>
  dispatch({ chatmailHeader, userId: getActiveUserId(getState), type: types.COMPOSER_SET_CHATMAIL_HEADER })

/**
 * @param {String} messageId
 */
const setReplyMessage = (messageId) => (dispatch, getState) =>
  dispatch({ messageId, userId: getActiveUserId(getState), type: types.COMPOSER_SET_REPLY_MESSAGE })

/**
 * @param {String} draftToEdit
 */
const setDraftToEdit = (draftToEdit) => (dispatch, getState) =>
  dispatch({ draftToEdit, userId: getActiveUserId(getState), type: types.COMPOSER_SET_DRAFT_TO_EDIT })

/**
 * @param {Object} currentSubject
 */
const setCurrentSubject = (currentSubject) => (dispatch, getState) =>
  dispatch({ currentSubject, userId: getActiveUserId(getState), type: types.COMPOSER_SET_CURRENT_SUBJECT })

/**
 * @param {Object} subject
 */
const updateStatusChatmail = (subject) => (dispatch, getState) =>
  dispatch({ subjectState: subject.state, userId: getActiveUserId(getState), type: types.USER_UPDATE_CHATMAIL })

const setFiles = (newFiles = []) => (dispatch, getState) =>
  dispatch({ newFiles, userId: getActiveUserId(getState), type: types.COMPOSER_SET_FILES })

const updateButtons = (buttons = []) => (dispatch, getState) =>
  dispatch({ buttons, userId: getActiveUserId(getState), type: types.COMPOSER_EDIT_BUTTONS })

const setSurvey = () => (dispatch, getState) =>
  dispatch({ userId: getActiveUserId(getState), type: types.COMPOSER_SET_SURVEY })

/**
 * Send message to user or dashboard
 * @param {String} message
 * @param {Boolean} messageType if true send message only in dashboard, false send message to user
 */
const sendMessage = (params, messageType, persistFields) => (dispatch, getState) => {
  const {
    message, attachments, checkTime, tag, buttons, vaSuggestion, nodeId, eventType, isSurvey,
    inReplyTo: messageId, to, cc, cco, subject, subjectId, draftId, variables
  } = params
  const { conversations, user: dashuser } = getState()

  const userId = getActiveUserId(getState)
  const {
    selectedTemplate, isDeferred, deferredDate, inReplyTo
  } = conversations[userId]?.composer || {}

  const data = {
    message,
    attachments: attachments.map(elem => {
      if (!elem.mime) delete elem.shortUrl
      if (elem.mimeSubtype && elem.mimeType) delete elem.mime

      return elem
    }),
    userId,
    dashuserId: dashuser._id,
    locale: dashuser.locale,
    vaSuggestion,
    nodeId,
    eventType
  }

  if (isDeferred) data.scheduledAt = deferredDate
  if (subjectId) data.subjectId = subjectId
  dispatch(send(userId))

  switch (messageType) {
    case 'note': {
      return conversationsApi.sendNote(data)
        .then(({ data }) => {
          const { existVirus } = verifyFileVirus({ data, dispatch, userId, updateFiles, sent })
          if(existVirus) throw data
          if (data.errorMessage) throw new Error(data.errorMessage)
          dispatch(resetComposer({ userId, persistFields }))
        })
        .catch(() => {
          dispatch(sent(userId, { error: 'send-note-dashboard-error' }))
          throw data
        })
    }
    case 'text':
    default: {
      if (selectedTemplate) data.templateId = selectedTemplate
      if (tag) data.tag = tag
      if (buttons) data.buttons = buttons
      if (attachments) data.attachments = attachments
      if (isSurvey) data.isSurvey = isSurvey
      if (inReplyTo || messageId) data.inReplyTo = inReplyTo || messageId
      if (to) data.to = to
      if (cc) data.cc = cc
      if (cco) data.cco = cco
      if (subject) data.subject = subject
      if (draftId) data.draftId = draftId
      if (variables?.length) {
        data.variables = variables
        variables.forEach(e => {
          data.message = data.message.replace(e.name, e.value || e.name)
        })
      }

      if (isDeferred) {
        data.userId = getState().conversations[userId].user._id
        return conversationsApi.sendDeferredMessage({ ...data, checkTime })
          .then(({ data }) => {
            const { existVirus } = verifyFileVirus({ data, dispatch, userId, updateFiles, sent })
            if(existVirus) throw data
            dispatch(resetComposer({ userId, persistFields }))
          })
          .catch(({ response: { data } }) => {
            dispatch(sent(userId, { error: data.type || 'send-message-user-error' }))
            throw data
          })
      } else {
        return conversationsApi.sendMessage({
          ...data,
          checkTime
        })
          .then(({ data }) => {
            const { existVirus } = verifyFileVirus({ data, dispatch, userId, attachments, updateFiles, sent })
            if(existVirus) throw data
            dispatch(resetComposer({ userId, persistFields }))
          })
          .catch(({ response: { data } }) => {
            dispatch(sent(userId, { error: data.type || 'send-message-user-error' }))
            throw data
          })
      }
    }
  }
}

/**
 * Update files
 * @param {Array} files
 */
const updateFiles = (userId, newFiles = [], replace) => ({
  userId,
  newFiles,
  replace,
  type: types.COMPOSER_ADD_FILES,
})

/**
 * Remove file from array
 * @param {Number} fileId The index of the file to remove
 */
const removeFile = (fileId) => (dispatch, getState) => dispatch({ fileId, userId: getActiveUserId(getState), type: types.COMPOSER_REMOVE_FILES })

/**
 * Upload files to s3
 * @param {Array} files
 */
const uploadFiles = (files, replace) => async (dispatch, getState) => {
  if (!files.length) return null

  const user = getActiveUserFromState(getState)
  const userId = getActiveUserId(getState)

  const company = getState().companies._id ? getState().companies : Object.values(getState().companies)

  const { type } = company

  dispatch(uploading(userId))

  const formData = new FormData()
  const dataBody = {
    companyType: type,
    typeUpload: 'userFile',
    user: user,
  }
  formData.append('data', JSON.stringify(dataBody))
  files.forEach(it => {
    formData.append('files', it.file)
  })

  return resource
    .uploadFiles(formData)
    .then(({ data, status }) => {
      dispatch(updateFiles(user.id, data.attachments, replace))
      return data.attachments
    })
    .catch(({ response = {}, message }) => {
      const { data = {}, status } = response
      const error = data.type ? data.type : status === 413 ? 'file-too-big' : 'upload-file-error'

      dispatch(sent(userId, { error }))

      throw error
    })
}

/**
 * Set AI suggestions
 * @param {string} userId
 * @param {[{
 *  logId: string,
 *  id: string,
 *  content: string,
 *  confidence: number,
 *  attachments: [object],
 *  buttons: [object]
 * }]} suggestions
 */
const setSuggestions = (userId, suggestions = []) => {
  if (!userId) return
  if (!Array.isArray(suggestions)) return
  if (!suggestions.length) return

  return {
    type: types.SET_SUGGESTIONS,
    userId,
    suggestions,
  }
}

const userStartSuggestions = new Set()

/**
 * Get AI suggestion by userId
 * @param {string} userId
 */
const getSuggestionsByUserId = (userId) => async (dispatch) => {
  if (userStartSuggestions.has(userId)) return
  userStartSuggestions.add(userId)

  const { hasSuggestions, suggestions } = await apiConversation.suggestions.getByConversationId({
    conversationId: userId
  })

  setTimeout(() => {
    userStartSuggestions.delete(userId)
  }, 2000)
  if (!hasSuggestions) return

  dispatch(setSuggestions(userId, suggestions))
}

/**
 * Reject AI suggestion
 * @param {string} userId
 * @param {object} suggestion
 * @param {object} suggestion.logId
 * @param {string} suggestion.id
 *
 * @returns Promise<boolean>
 */
const rejectSuggestion = (userId, suggestion) => async (dispatch) => {
  dispatch({
    userId,
    type: types.REJECT_SUGGESTIONS,
    suggestion
  })

  try {
    if (userId && suggestion.logId) {
      await apiConversation.suggestions.rejectById({
        conversationId: userId,
        logId: suggestion.logId,
        suggestionId: suggestion.id
      })
    }
  } catch(error) {
    console.error(userId, suggestion.logId, error)
  }

  return true
}

const toggleDeferred = (isDeferred) => (dispatch, getState) => dispatch({ isDeferred, userId: getActiveUserId(getState), type: types.TOGGLE_DEFERRED })
const setDeferredDate = (deferredDate) => (dispatch, getState) => dispatch({ deferredDate, userId: getActiveUserId(getState), type: types.SET_DEFERRED_DATE })

/**
 * Update Wall for SMS state
 * @param {Boolean} isWallEnabledForSMS
 */
const setWallEnabledForSMS = ({ isWallEnabledForSMS }) => (dispatch, getState) =>
  dispatch({ isWallEnabledForSMS, userId: getActiveUserId(getState), type: types.COMPOSER_SET_WALL_ENABLED_SMS })

/**
 * Set variables
 * @param {Array} variables
 */
const setVariables = (variables) => (dispatch, getState) =>
  dispatch({
    userId: getActiveUserId(getState),
    variables,
    type: types.COMPOSER_SET_VARIABLES
  })

export default {
  changeMessageType,
  updateMessage,
  uploadFiles,
  removeFile,
  sendMessage,
  resetComposer,
  setRelatedTag,
  setMessageFromTemplate,
  setSuggestions,
  rejectSuggestion,
  updateButtons,
  setSurvey,
  setFiles,
  addExternalText,
  updateMsgFromExternalText,
  setChatmailHeader,
  setReplyMessage,
  setDraftToEdit,
  setCurrentSubject,
  toggleDeferred,
  setDeferredDate,
  updateFiles,
  updateStatusChatmail,
  setWallEnabledForSMS,
  setVariables,
  setReply,
  removeReply,
  getSuggestionsByUserId
}
